import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Building2, CreditCard, Bell, Users, Bug, Database, Mail, CheckCircle2, XCircle, History, Zap } from 'lucide-react';
import { adminApi, settingsApi, email as emailApi } from '../../lib/api';
import { useConfig } from '../../context/ConfigContext';
import TeamManager, { ChangePasswordCard } from './TeamManager';
import AuditLog from './AuditLog';
import AutomationsPanel from './AutomationsPanel';
import { toast } from 'sonner';

const NOTIFS = [
  { key: 'notifyNewWorkOrders', l: 'New work orders', d: 'Notify when a new job is booked' },
  { key: 'notifyInvoicePaid', l: 'Invoice paid', d: 'Email when customer pays an invoice' },
  { key: 'notifyOverdue', l: 'Overdue alerts', d: 'Daily summary of overdue invoices' },
  { key: 'notifyTechCheckins', l: 'Tech check-ins', d: 'Real-time tech start/finish updates' },
];

export default function SettingsPage() {
  const { demoMode, contact } = useConfig();
  const [seeding, setSeeding] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  const [reimportResult, setReimportResult] = useState(null);
  const [s, setS] = useState({});
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    settingsApi.get().then(setS).catch(() => {});
    emailApi.status().then(setEmailStatus).catch(() => {});
  }, []);
  const set = (k, v) => setS((o) => ({ ...o, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const r = await settingsApi.update(s); setS(r); toast.success('Settings saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed (admin only)'); }
    finally { setSaving(false); }
  };
  const onLoadDemo = async () => {
    setSeeding(true);
    try { const r = await adminApi.seedDemo(); toast.success(r.message || 'Demo data loaded'); }
    catch { toast.error('Could not load demo data'); }
    finally { setSeeding(false); }
  };

  const onReimport = async () => {
    if (!window.confirm('This will REPLACE current customers, invoices, work orders and payments with your CSV snapshot. Continue?')) return;
    setReimporting(true);
    try { const r = await adminApi.bulkSeedCsv(); setReimportResult(r); toast.success('Your data was re-imported'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Import failed (admin only)'); }
    finally { setReimporting(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-5xl">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your company, team, and integrations.</p>
      </div>
      <Tabs defaultValue="company">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="company" className="gap-1.5"><Building2 className="w-4 h-4" />Company</TabsTrigger>
          <TabsTrigger value="billing" className="gap-1.5"><CreditCard className="w-4 h-4" />Billing</TabsTrigger>
          <TabsTrigger value="notifications" className="gap-1.5"><Bell className="w-4 h-4" />Notifications</TabsTrigger>
          <TabsTrigger value="automations" className="gap-1.5"><Zap className="w-4 h-4" />Automations</TabsTrigger>
          <TabsTrigger value="team" className="gap-1.5"><Users className="w-4 h-4" />Team</TabsTrigger>
          <TabsTrigger value="audit" className="gap-1.5"><History className="w-4 h-4" />Audit Log</TabsTrigger>
          <TabsTrigger value="data" className="gap-1.5"><Bug className="w-4 h-4" />Data</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-5">
          <Card className="p-6 border-slate-200 space-y-5" data-testid="company-settings">
            <div className="grid md:grid-cols-2 gap-5">
              <div><Label>Company Name</Label><Input value={s.companyName || ''} onChange={(e) => set('companyName', e.target.value)} className="mt-1.5" data-testid="company-name-input" /></div>
              <div><Label>Phone</Label><Input value={s.phone || ''} onChange={(e) => set('phone', e.target.value)} className="mt-1.5" /></div>
              <div><Label>Email</Label><Input value={s.email || ''} onChange={(e) => set('email', e.target.value)} className="mt-1.5" /></div>
              <div><Label>Website</Label><Input value={s.website || ''} onChange={(e) => set('website', e.target.value)} className="mt-1.5" /></div>
              <div><Label>Address</Label><Input value={s.address || ''} onChange={(e) => set('address', e.target.value)} className="mt-1.5" /></div>
              <div><Label>City, State Zip</Label><Input value={s.cityStateZip || ''} onChange={(e) => set('cityStateZip', e.target.value)} className="mt-1.5" /></div>
              <div><Label>License #</Label><Input value={s.license || ''} onChange={(e) => set('license', e.target.value)} className="mt-1.5" /></div>
              <div><Label>Time Zone</Label><Input value={s.timezone || ''} onChange={(e) => set('timezone', e.target.value)} className="mt-1.5" /></div>
              {/* Terms drive the whole collections ageing: an invoice falls due
                  this many days after the service. They were not stored at all,
                  so every invoice aged as zero days past due. */}
              <div>
                <Label>Payment terms (days)</Label>
                <Input type="number" min="0" inputMode="numeric" value={s.paymentTermsDays ?? 30}
                  onChange={(e) => set('paymentTermsDays', e.target.value === '' ? '' : Number(e.target.value))}
                  className="mt-1.5" data-testid="payment-terms-input" />
                <p className="text-xs text-slate-500 mt-1">Days from the service date before an invoice is past due. Net 30 by default.</p>
              </div>
              <div>
                <Label>Late fee</Label>
                <Input type="number" min="0" step="0.01" inputMode="decimal" value={s.lateFeeAmount ?? 20}
                  onChange={(e) => set('lateFeeAmount', e.target.value === '' ? '' : Number(e.target.value))}
                  className="mt-1.5" data-testid="late-fee-input" />
                <p className="text-xs text-slate-500 mt-1">Quoted on collection letters and reminders. Not charged to an account automatically.</p>
              </div>
              {/* The service-report rule. It holds the invoice, never the job:
                  a technician always closes a visit out, and the day's end-of-day
                  report lists anything still owing its report. */}
              <div className="sm:col-span-2">
                <label className="flex items-start gap-3">
                  <input type="checkbox" className="mt-0.5 w-5 h-5 accent-[#8BC53F]"
                    checked={s.requireServiceReport !== false}
                    onChange={(e) => set('requireServiceReport', e.target.checked)}
                    data-testid="require-service-report" />
                  <span className="text-sm text-slate-700">
                    <span className="font-semibold">Require a service report before invoicing</span>
                    <span className="block text-xs text-slate-500">
                      A completed visit is invoiced once the technician files its electronic service
                      report, including the pesticides applied (or a note that none were). Jobs still
                      owing one appear on the End of Day page.
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="save-company-btn">{saving ? 'Saving…' : 'Save Changes'}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="mt-5 space-y-5">
          <Card className="p-6 border-slate-200">
            <div className="flex items-center gap-2 font-bold text-slate-900 mb-3"><Mail className="w-4 h-4 text-[#8BC53F]" />Email Sending (Resend)</div>
            {emailStatus?.configured ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4" />Connected · sending from <b className="ml-1">{emailStatus.sender}</b></div>
            ) : (
              <div className="text-sm text-slate-600">
                <div className="flex items-center gap-2 text-amber-700 mb-1"><XCircle className="w-4 h-4" />Not connected</div>
                Add <code className="bg-slate-100 px-1 rounded">RESEND_API_KEY</code> and <code className="bg-slate-100 px-1 rounded">SENDER_EMAIL</code> to the backend environment to send real invoice & estimate emails.
              </div>
            )}
          </Card>
          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between">
              <div><div className="font-bold text-slate-900">Card payments</div><div className="text-sm text-slate-500">Stripe Checkout — set your live STRIPE_API_KEY in the deployment environment.</div></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-5">
          <Card className="p-6 border-slate-200 space-y-4" data-testid="notif-settings">
            {NOTIFS.map((n) => (
              <div key={n.key} className="flex items-center justify-between">
                <div><div className="font-semibold text-slate-900">{n.l}</div><div className="text-sm text-slate-500">{n.d}</div></div>
                <Switch checked={!!s[n.key]} onCheckedChange={(v) => set(n.key, v)} />
              </div>
            ))}
            <Button onClick={save} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]">{saving ? 'Saving…' : 'Save Preferences'}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="automations" className="mt-5">
          <AutomationsPanel />
        </TabsContent>

        <TabsContent value="team" className="mt-5 space-y-5">
          <TeamManager />
          <ChangePasswordCard />
        </TabsContent>

        <TabsContent value="audit" className="mt-5">
          <AuditLog />
        </TabsContent>

        <TabsContent value="data" className="mt-5">
          {demoMode && (
            <Card className="p-6 border-[#8BC53F]/40 bg-[#8BC53F]/5 space-y-3 mb-5" data-testid="demo-data-card">
              <div className="flex items-center gap-2 font-bold text-slate-900"><Database className="w-4 h-4 text-[#8BC53F]" />Demo Mode Active</div>
              <p className="text-sm text-slate-600">This is the public/demo build (contact info shows <b>{contact.email}</b>). Load fresh sample customers, invoices and work orders in one click.</p>
              <Button onClick={onLoadDemo} disabled={seeding} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="load-demo-data-btn"><Database className="w-4 h-4" />{seeding ? 'Loading…' : 'Load Demo Data'}</Button>
            </Card>
          )}
          <Card className="p-6 border-slate-200 space-y-3">
            <div className="font-bold text-slate-900">Your data is stored in your account database</div>
            <p className="text-sm text-slate-500">Customers, invoices, work orders, payments, contracts, inventory, leads, and accounting all live in your secure database — synced across every device and login.</p>
            <div className="mt-2 pt-4 border-t border-slate-100">
              <div className="font-bold text-slate-900 text-sm">Importing CSV data</div>
              <p className="text-xs text-slate-500 mt-1">Every list page (Customers, Invoices, Leads, Inventory, Contracts, etc.) has an <b>Import CSV</b> button. Export first to see the exact column headers expected.</p>
            </div>
          </Card>
          {/* The one-click "Re-import my data" card was removed: it erased every customer,
              invoice, job and payment and reloaded a June snapshot. */}
        </TabsContent>
      </Tabs>
    </div>
  );
}
